import React from "react";

import { Source } from "Constant/source";

import {
    CreationFooter,
    FooterSubmitLogo,
} from "components/organisms/CreationModal";

import {
    APP_CAPI_SOURCE_TYPES,
    FOOTER_ACTION_SOURCE_TYPES,
    SFTP_SOURCE_TYPES,
} from "../../SourceCreation.constants";
import SourceFormFooter from "../SourceFormFooter";

/**
 * Footer for the configure step.
 *
 * - Generic forms, SFTP/FTP and App CAPI are driven directly from host state.
 * - CRM / OAuth forms (`FOOTER_ACTION_SOURCE_TYPES`) register their non-OAuth
 *   primary action via `registerFooterAction`; it is rendered from
 *   `footerAction`. While nothing is registered (e.g. the OAuth connect
 *   button in the content is the only action) only Back / Cancel are shown.
 */
const SourceCreationFooter = ({ sourceCreation, onBack }) => {
    const {
        language,
        form,
        sftp,
        appCapi,
        upload,
        loading,
        canChangeType,
        onCancel,
        footerAction,
    } = sourceCreation;
    const { sourceType } = form;

    if (!sourceType) return null;

    const isUpload = sourceType === Source.upload;
    const isSftp = SFTP_SOURCE_TYPES.includes(sourceType);
    const isAppCapi = APP_CAPI_SOURCE_TYPES.includes(sourceType);
    const usesFooterAction = FOOTER_ACTION_SOURCE_TYPES.includes(sourceType);
    const backHandler = canChangeType ? onBack : undefined;

    if (isSftp) {
        if (sftp.isConnected) {
            return (
                <SourceFormFooter
                    language={language}
                    onBack={backHandler}
                    onCancel={onCancel}
                    onSubmit={sftp.createSource}
                    isCancelDisabled={sftp.isLoading}
                    isSubmitLoading={sftp.isLoading}
                />
            );
        }
        return (
            <SourceFormFooter
                language={language}
                onBack={backHandler}
                onCancel={onCancel}
                onSubmit={sftp.connect}
                submitLabel={language?.buttons?.connect ?? "Connect"}
                isCancelDisabled={sftp.isLoading}
                isSubmitLoading={sftp.isLoading}
            />
        );
    }

    if (isAppCapi) {
        return (
            <SourceFormFooter
                language={language}
                onBack={backHandler}
                onCancel={onCancel}
                onSubmit={appCapi.create}
                isCancelDisabled={appCapi.isLoading}
                isSubmitLoading={appCapi.isLoading}
            />
        );
    }

    if (usesFooterAction) {
        if (!footerAction) {
            return (
                <CreationFooter
                    onBack={backHandler}
                    backLabel={language?.buttons?.back ?? "Back"}
                    onCancel={onCancel}
                    cancelLabel={language?.buttons?.cancel ?? "Cancel"}
                    hideSubmit
                />
            );
        }
        return (
            <SourceFormFooter
                language={language}
                onBack={backHandler}
                onCancel={onCancel}
                onSubmit={footerAction.onSubmit}
                submitLabel={footerAction.label}
                submitIcon={
                    footerAction.iconSrc ? (
                        <FooterSubmitLogo src={footerAction.iconSrc} alt="" />
                    ) : undefined
                }
                isCancelDisabled={footerAction.isLoading}
                isSubmitDisabled={footerAction.isDisabled}
                isSubmitLoading={footerAction.isLoading}
            />
        );
    }

    return (
        <SourceFormFooter
            language={language}
            onBack={backHandler}
            onCancel={onCancel}
            onSubmit={sourceCreation.handleCreateSource}
            isCancelDisabled={loading}
            isSubmitDisabled={
                isUpload
                    ? !upload.name || !upload.file || upload.isLoading
                    : loading
            }
            isSubmitLoading={isUpload ? upload.isLoading : loading}
        />
    );
};

export default SourceCreationFooter;
